import { z } from 'zod';
import { COLLECTOR_CHANNELS } from './collector.schema';
import { isoDateTimeSchema } from './common.schema';
/**
 * Collector release contract (#7). A release job publishes, per channel, a signed manifest
 * (`channels/<channel>/manifest.json` + `.sig`, an ed25519 signature made with a Cloud KMS key over the exact
 * manifest bytes) and immutable artifacts (`releases/<version>/...`) to the env's PRIVATE bucket (collectors fetch through short-lived signed URLs from ingest).
 *
 * Three parties read this shape: the release job writes it, the API reads it (an UNVERIFIED hint, to advertise
 * the target version in config sync), and the collector reads it AND verifies the signature before trusting any
 * of it. The Go parser (`collector/internal/update`) is checked against the same committed fixture
 * (`shared/fixtures/collector-release-manifest.v1.json`).
 */
export const COLLECTOR_RELEASE_PLATFORMS = ['linux-amd64', 'windows-amd64'];
/** `1.2.3` or `1.2.3-rc.1`: strict semver without a leading `v` or build metadata, numeric parts without leading zeros
 *  and at most 9 digits (so every parser, JavaScript and Go alike, reads the same number). Keep in step with
 *  release-lib.mjs and collector/internal/update/version.go. */
export const COLLECTOR_VERSION_RE = /^(?:0|[1-9]\d{0,8})\.(?:0|[1-9]\d{0,8})\.(?:0|[1-9]\d{0,8})(?:-(?:0|[1-9]\d{0,8}|[0-9A-Za-z]*[A-Za-z][0-9A-Za-z]*)(?:\.(?:0|[1-9]\d{0,8}|[0-9A-Za-z]*[A-Za-z][0-9A-Za-z]*))*)?$/;
export const collectorReleaseArtifactSchema = z
    .object({
    /** Relative to the bucket root; never absolute, never traversing. */
    path: z.string().regex(/^releases\/[0-9A-Za-z][0-9A-Za-z.-]*\/detect-collector-(linux|windows)-amd64(\.exe)?$/),
    sha256: z.string().regex(/^[a-f0-9]{64}$/, 'must be lowercase SHA-256 hex'),
    size: z.number().int().positive().max(200 * 1024 * 1024),
})
    .strict();
export const collectorReleaseManifestSchema = z
    .object({
    schemaVersion: z.literal(1),
    channel: z.enum(COLLECTOR_CHANNELS),
    version: z.string().regex(COLLECTOR_VERSION_RE),
    publishedAt: isoDateTimeSchema,
    /** The bucket this manifest was published to. Signed, so it cannot be copied to another bucket (another
     *  environment's, say) and still be accepted: collectors and the API require it to match where they fetched it. */
    bucket: z.string().regex(/^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/),
    /** The exact commit on `main` the artifacts were built from. */
    sourceSha: z.string().regex(/^[a-f0-9]{40}$/),
    artifacts: z
        .object({
        'linux-amd64': collectorReleaseArtifactSchema,
        'windows-amd64': collectorReleaseArtifactSchema,
    })
        .strict(),
})
    .strict()
    .superRefine((m, ctx) => {
    for (const platform of COLLECTOR_RELEASE_PLATFORMS) {
        const want = `releases/${m.version}/detect-collector-${platform}${platform.startsWith('windows') ? '.exe' : ''}`;
        if (m.artifacts[platform].path !== want) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['artifacts', platform, 'path'],
                message: `must be ${want}`,
            });
        }
    }
});
/** What `GET /config` advertises: only that a release exists for the channel (a hint to ask for it). Null = no release.
 *  The bucket is private, so config carries no URL; the URLs come from `GET /release` (below) when the collector acts. */
export const collectorReleaseAdvertSchema = z
    .object({
    version: z.string().regex(COLLECTOR_VERSION_RE),
})
    .strict();
const signedUrlSchema = z.string().url().startsWith('https://').max(2048);
/**
 * What `GET /release` answers an authenticated collector: short-lived (15 minute), GET-only V4 signed URLs for the
 * channel's manifest, its signature and each platform's artifact (#7 private releases). The URLs are access, not trust:
 * the collector still verifies the manifest's Ed25519 signature against the keys compiled into its binary, and requires
 * the artifact URL to name exactly the path the SIGNED manifest lists, so a server that signs a different object gains
 * nothing.
 */
export const collectorSignedReleaseSchema = z
    .object({
    version: z.string().regex(COLLECTOR_VERSION_RE),
    manifestUrl: signedUrlSchema,
    manifestSigUrl: signedUrlSchema,
    artifactUrls: z.object({ 'linux-amd64': signedUrlSchema, 'windows-amd64': signedUrlSchema }).strict(),
})
    .strict();
//# sourceMappingURL=collector-release.schema.js.map